import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { ga4 } from "../analytics/ga4";
import { pathFor } from "../app/paths";
import { COOKIE_POLICY_URL, TERMS_URL } from "../config/legalLinks";

// Privacy: pagina custom interna (/:locale/privacy). Cookie Policy e Termini su Iubenda.

export function Footer() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const base = `/${locale || "it"}`;

  const linkStyle: React.CSSProperties = {
    color: "rgba(255,255,255,.7)",
    textDecoration: "none",
    fontSize: 11,
    letterSpacing: 0.3,
    transition: "color 0.2s ease",
    // Un bersaglio da toccare deve essere alto almeno 24 pixel: sotto quella
    // soglia si sbaglia link, e su un footer con cinque voci vicine si sbaglia
    // spesso. L'aspetto non cambia — cresce solo l'area sensibile al tocco,
    // che è invisibile.
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
  };

  return (
    <footer style={{ background: cfg.color_dark, color: "rgba(255,255,255,.75)" }}>
      {/* ── Blog strip ── */}
      <div style={{ padding: isMobile ? "20px 24px" : "24px 48px", background: cfg.color_footer_blog || cfg.color_dark }}>
        <div style={{
          maxWidth: 1200, margin: "0 auto",
          display: "flex", alignItems: "center", gap: isMobile ? 14 : 28,
          flexDirection: isMobile ? "column" : "row",
        }}>
          <div style={{ width: isMobile ? "100%" : 160, height: isMobile ? 80 : 80, flexShrink: 0, overflow: "hidden", background: "rgba(255,255,255,0.03)" }}>
            <img
              src={resolveImageUrl(cfg.blog_image_url) || "/images/blog-preview.jpg"}
              alt={t("blog.subtitle")}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 1 }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 3px" }}>
              {t("blog.title")}
            </p>
            <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 15 : 17, fontWeight: 400, color: "#fff", margin: "0 0 3px", lineHeight: 1.2 }}>
              {t("blog.subtitle")}
            </h3>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,.62)", margin: 0, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
              {t("blog.description")}
            </p>
          </div>
          <Link
            to={pathFor("blog", locale)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              color: "#fff", textDecoration: "none", fontSize: 9, fontWeight: 600,
              letterSpacing: 1.5, textTransform: "uppercase",
              border: "1px solid rgba(255,255,255,.1)", padding: "7px 16px",
              transition: "background 0.2s", whiteSpace: "nowrap", flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            {t("footer.readBlog")}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
        </div>
      </div>

      {/* ── Main footer ── */}
      <div style={{ padding: isMobile ? "24px 24px 18px" : "28px 48px 24px" }}>
        <div style={{
          maxWidth: 1200, margin: "0 auto",
          display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2.5fr 1fr 1fr",
          gap: isMobile ? 24 : 48,
        }}>
          <div>
            <img
              src={cfg.footer_logo_url ? resolveImageUrl(cfg.footer_logo_url) : (cfg.logo_url ? resolveImageUrl(cfg.logo_url) : "/granborgo-logo.svg")}
              alt={cfg.brand_name}
              style={{ height: 28, objectFit: "contain", display: "block", marginBottom: 6, filter: "brightness(10)" }}
            />
            <p style={{ fontSize: 11, lineHeight: 1.5, margin: 0, maxWidth: 280, color: "rgba(255,255,255,.65)" }}>
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <p style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,.62)", margin: "0 0 10px" }}>
              {t("footer.navigate")}
            </p>
            <nav style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Link to={base} style={linkStyle}>{t("nav.home")}</Link>
              <Link to={pathFor("collections", locale)} style={linkStyle}>{t("nav.confezioni")}</Link>
              <Link to={pathFor("about", locale)} style={linkStyle}>{t("nav.about")}</Link>
              <Link to={pathFor("fiscal", locale)} style={linkStyle}>{t("nav.fiscalita")}</Link>
              <Link to={pathFor("blog", locale)} style={linkStyle}>{t("nav.blog")}</Link>
            </nav>
          </div>

          <div>
            <p style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,.62)", margin: "0 0 10px" }}>
              {t("footer.contact")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, lineHeight: 1.5 }}>
              {cfg.contact_email && <a href={`mailto:${cfg.contact_email}`} onClick={() => ga4.contactClick("email", "footer")} style={linkStyle}>{cfg.contact_email}</a>}
              {cfg.contact_phone && <a href={`tel:${cfg.contact_phone.replace(/\s/g, "")}`} onClick={() => ga4.contactClick("phone", "footer")} style={linkStyle}>{cfg.contact_phone}</a>}
              {cfg.contact_address && <p style={{ margin: 0, whiteSpace: "pre-line", color: "rgba(255,255,255,.62)", fontSize: 11 }}>{cfg.contact_address}</p>}
              {/* P.IVA / VAT — editabile da dashboard (Impostazioni → Contatti) */}
              {cfg.vat && <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,.62)", fontSize: 11 }}>{cfg.vat}</p>}
            </div>
          </div>
        </div>

        <div style={{
          maxWidth: 1200, margin: "0 auto",
          marginTop: isMobile ? 20 : 24, paddingTop: 12,
          fontSize: 10, color: "rgba(255,255,255,.62)", textAlign: "center", letterSpacing: 0.5,
        }}>
          &copy; {new Date().getFullYear()} {cfg.brand_name}. {t("footer.rights")}
          {" · "}
          <Link
            to={pathFor("privacy", locale)}
            style={{ color: "inherit", textDecoration: "underline", display: "inline-block", minHeight: 24, lineHeight: "24px" }}
          >
            {t("footer.privacy")}
          </Link>
          {" · "}
          {/* La Privacy Policy rimanda alla «cookies policy nel footer del
              sito»: deve esserci davvero, non solo dentro il banner. */}
          <a
            href={COOKIE_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline", display: "inline-block", minHeight: 24, lineHeight: "24px" }}
          >
            {t("cookie.policyLink")}
          </a>
          {" · "}
          {/* Obbligatorio per un'azienda tedesca e raggiungibile da ogni
              pagina: la legge chiede che sia «facilmente riconoscibile e
              direttamente accessibile», e il footer è l'unico posto che
              soddisfa entrambe le condizioni su tutto il sito. */}
          <Link
            to={pathFor("impressum", locale)}
            style={{ color: "inherit", textDecoration: "underline", display: "inline-block", minHeight: 24, lineHeight: "24px" }}
          >
            {t("footer.impressum")}
          </Link>
          {" · "}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline", display: "inline-block", minHeight: 24, lineHeight: "24px" }}
          >
            {t("footer.terms")}
          </a>
          {" · "}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("gb:open-cookie"))}
            style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer", textDecoration: "underline", minHeight: 24, lineHeight: "24px" }}
          >
            {t("cookie.manage")}
          </button>
        </div>
      </div>
    </footer>
  );
}
